import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { ChildEvent, LabelValuePair, ScrBaseComponent } from 'src/app/components/base.component';
import { AddCreditCardValidators } from 'src/app/directives/credit-card.directive';
import { FirstNameValidatorDirective, LastNameValidatorDirective } from 'src/app/directives/validators.directive';
import { Card } from 'src/app/models/payment.model';
import { ConfigService, Country, Region } from '../../../../../services';
import { PaymentMethodsService } from '../../../../../services/paymentmethod.service';
import { MaxLen } from '../../../../../shared/constants';

@Component({
  selector: 'app-update-card',
  templateUrl: './update-card.component.html',
  styleUrls: ['./update-card.component.scss']
})
export class UpdateCardComponent extends ScrBaseComponent implements OnInit {

  @Input() card!: Card;
  @Input() hasDefault!: boolean;
  @Output() emitAction = new EventEmitter<ChildEvent>();
  editCardForm!: UntypedFormGroup;
  submitted = false;
  countries: Country[] = [];
  public regionList: Region[] = [];
  isDefaultCard?: boolean = false;
  months!: LabelValuePair[];
  years: LabelValuePair[] = [];
  dynamicMask!: string;


  constructor(private formBuilder: UntypedFormBuilder,
    private configService: ConfigService,
    private confirmService: ConfirmationService,
    private cardService: PaymentMethodsService) {
    super();

  }

  ngOnInit(): void {
    this.isDefaultCard = this.card.default;
    this.createFormControls();
    // const elmt = document.getElementById('editCardDiv');
    // window.scroll({top: elmt?.getBoundingClientRect().top, left:elmt?.getBoundingClientRect().left, behavior: 'smooth'});
    window.scroll(0, 0);

    this.configService.getCountries().subscribe(countries => {
      this.countries = countries;
      this.getRegions(this.card.country);
    },
      (error: HttpErrorResponse) => {
        this.processHttpError(error); this.jumpToTop();
      });
  }

  createFormControls() {

    const currentDate = new Date();

    this.months = [
      { label: "01", value: 1 },
      { label: "02", value: 2 },
      { label: "03", value: 3 },
      { label: "04", value: 4 },
      { label: "05", value: 5 },
      { label: "06", value: 6 },
      { label: "07", value: 7 },
      { label: "08", value: 8 },
      { label: "09", value: 9 },
      { label: "10", value: 10 },
      { label: "11", value: 11 },
      { label: "12", value: 12 }
    ];

    for (let x = 0; x < 12; x++) {
      this.years.push({ label: (currentDate.getFullYear() + x).toString(), value: currentDate.getFullYear() + x });
    }
    this.editCardForm = this.formBuilder.group(
      {
        firstName: [this.card.firstName, [Validators.required, Validators.maxLength(MaxLen.FirstName), AddCreditCardValidators.WhiteSpaceValidation(), new FirstNameValidatorDirective().fct()]],
        lastName: [this.card.lastName, [Validators.required, Validators.maxLength(MaxLen.LastName), AddCreditCardValidators.WhiteSpaceValidation(), new LastNameValidatorDirective().fct()]],
        address1: [this.card.address1, [Validators.required, Validators.maxLength(MaxLen.Address), AddCreditCardValidators.WhiteSpaceValidation()]],
        address2: [this.card.address2, [Validators.maxLength(MaxLen.Address), AddCreditCardValidators.WhiteSpaceValidation()]],
        city: [this.card.city, [Validators.required, Validators.maxLength(MaxLen.City), AddCreditCardValidators.WhiteSpaceValidation()]],
        state: [this.card.state],
        zip: [this.card.zip, AddCreditCardValidators.WhiteSpaceValidation()],
        expirationMonth: [parseInt(this.card.expirationMonth.toString()), [Validators.required]],
        expirationYear: [parseInt(this.card.expirationYear.toString()), [Validators.required]],
        description: [this.card.description, [Validators.maxLength(30)]],
        cardType: [this.card.cardType, [Validators.required, Validators.maxLength(32)]],
        country: [this.card.country, [Validators.required]],
        creditCardId: [this.card.creditCardId],
        lastFourDigits: [this.card.lastFourDigits],
        // cvv: [this.card.cvv, [Validators.required, Validators.minLength(3), Validators.maxLength(4)]],
        // creditCardNumber: [this.card.creditCardNumber, [Validators.required, CreditCardValidators.validateCCNumber]],
        default: [this.card.default]
      },
      {
        validators: [AddCreditCardValidators.CheckExpirationDate(), AddCreditCardValidators.CustomRequired(), AddCreditCardValidators.CustomZipInvalid()],
        updateOn: 'blur'
      });
  }

  getRegions(value: string, resetZipState?: boolean) {

    if (value) {
      this.configService.getRegions(value).subscribe(regions => {
        this.regionList = regions as Region[];
      });
    }
    if (value === 'ca') {
      this.dynamicMask = 'S0S 0S0';
    } else {
      this.dynamicMask = '';
    }


    if (resetZipState) {
      this.editCardForm.get('zip')?.reset();
      this.editCardForm.get('state')?.reset();
    }


  }

  submit() {
    this.clearMessages();
    this.submitted = true;

    if (!this.editCardForm.valid) {
      this.scrollToFirstError();
      return;
    }

    const formData = JSON.parse(JSON.stringify(this.editCardForm.value), this.prepareForSubmit);

    this.observe(this.cardService.updateCreditCard(formData), res => {
      if (res.messages && res.messages.length > 0) {
        this.emitAction.emit({ action: 'reload', value: 'reload', message: res.messages[0].message });
      }
      else {
        this.emitAction.emit({ action: 'reload', value: 'reload', messageCode: 'scr.cc.updated' });
      }
      return false;
    });
  }

  prepareForSubmit(key: string, value: string | number | boolean) {

    if (typeof value === 'string') {
      if (key === 'creditCardNumber') {
        return value.split(' ').join('');
      }
      return value.trim();
    }
    return value;
  }

  confirmCancelDialog() {
    this.confirmService.confirm({
      key: 'confirmCancel',
      header: 'Confirm Cancellation',
      message: 'Are you sure you want to cancel the request?',
      accept: () => {
        this.editCardForm.reset();
        this.emitAction.emit({ action: 'cancel', value: 'reload' });
        this.submitted = false;
      },
      reject: () => {
        this.confirmService.close();
      }
    });
  }
}
